/**
 * Doctor registration queue.
 *
 * Replaces `renderRegistrationsList`, `handleApproveRegistration`,
 * `handleRejectRegistration` and `updateRegistrationsBadge`.
 *
 * "Reject" maps to PUT /api/admin/doctors/:id/pending — the backend has no
 * delete or reject state, so a rejected application simply stays pending. That
 * is the honest behaviour, and the copy says so rather than implying deletion.
 */

import { useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Modal,
  PageHeader,
  Panel,
  Spinner,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useAsync } from '../../hooks/useAsync';
import { adminApi } from '../../api/endpoints';
import { categoryName } from '../../domain/catalog';
import { formatCurrency } from '../../domain/pricing';
import { ARABIC_DAYS } from '../../domain/dates';

export function AdminRegistrations() {
  const { success, error: toastError } = useNotifications();
  const state = useAsync(() => adminApi.pendingDoctors(), [], { initialData: [] });
  const pending = state.data || [];

  const [reviewing, setReviewing] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const approve = async () => {
    if (!approveTarget) return;
    setBusy(true);
    try {
      await adminApi.approveDoctor(approveTarget.id);
      success(`تم تفعيل حساب ${approveTarget.name}`);
      setApproveTarget(null);
      setReviewing(null);
      state.reload();
    } catch (err) {
      toastError(err.message || 'تعذر تفعيل الحساب');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="طلبات التسجيل"
        subtitle={`${pending.length} طلب بانتظار المراجعة`}
        icon="user-plus"
        actions={
          <Button variant="ghost" icon="refresh-cw" onClick={state.reload} loading={state.loading}>
            تحديث
          </Button>
        }
      />

      {state.error ? (
        <ErrorState error={state.error} onRetry={state.reload} />
      ) : state.loading && pending.length === 0 ? (
        <Spinner label="جاري تحميل الطلبات..." />
      ) : pending.length === 0 ? (
        <Panel>
          <EmptyState
            icon="inbox"
            title="لا توجد طلبات معلقة"
            message="كل طلبات تسجيل الأطباء تمت مراجعتها."
          />
        </Panel>
      ) : (
        <div className="space-y-3">
          {pending.map((doctor) => {
            const profile = doctor.DoctorProfile || {};
            return (
              <Panel key={doctor.id} className="flex flex-wrap items-center gap-4">
                <Avatar src={profile.image} name={doctor.name} size={48} />

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate font-bold text-white">{doctor.name}</h3>
                    <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
                      قيد المراجعة
                    </Badge>
                  </div>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-400">
                    <span dir="ltr">@{doctor.username}</span>
                    {profile.categoryId && <span>{categoryName(profile.categoryId)}</span>}
                    {doctor.phone && <span dir="ltr">{doctor.phone}</span>}
                  </p>
                </div>

                <div className="flex shrink-0 gap-2">
                  <Button variant="ghost" icon="eye" onClick={() => setReviewing(doctor)}>
                    مراجعة
                  </Button>
                  <Button variant="success" icon="check" onClick={() => setApproveTarget(doctor)}>
                    تفعيل
                  </Button>
                </div>
              </Panel>
            );
          })}
        </div>
      )}

      {/* Full application */}
      <Modal
        open={Boolean(reviewing)}
        onClose={() => setReviewing(null)}
        title="مراجعة طلب التسجيل"
        icon="file-search"
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setReviewing(null)}>
              إغلاق
            </Button>
            <Button variant="success" icon="check" onClick={() => setApproveTarget(reviewing)}>
              تفعيل الحساب
            </Button>
          </div>
        }
      >
        {reviewing && <RegistrationDetails doctor={reviewing} />}
      </Modal>

      <ConfirmDialog
        open={Boolean(approveTarget)}
        onClose={() => setApproveTarget(null)}
        onConfirm={approve}
        loading={busy}
        title="تفعيل حساب الطبيب"
        confirmLabel="تفعيل"
        message={`سيتمكن ${approveTarget?.name || 'الطبيب'} من تسجيل الدخول فوراً، وستظهر صفحته للمرضى للحجز.`}
      />
    </div>
  );
}

function RegistrationDetails({ doctor }) {
  const profile = doctor.DoctorProfile || {};
  const clinics = doctor.Clinics || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-4">
        <Avatar src={profile.image} name={doctor.name} size={64} />
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold text-white">{doctor.name}</h3>
          <p className="text-sm text-slate-400">{profile.title || 'طبيب'}</p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-3 rounded-xl border border-white/5 bg-white/5 p-3 text-sm">
        <Item label="اسم المستخدم" value={doctor.username} ltr />
        <Item label="التخصص" value={categoryName(profile.categoryId)} />
        <Item label="البريد" value={doctor.email || '—'} ltr />
        <Item label="الهاتف" value={doctor.phone || '—'} ltr />
        <Item label="سعر الكشف" value={formatCurrency(profile.priceExam)} />
        <Item label="سعر المتابعة" value={formatCurrency(profile.priceFollowup)} />
        <Item
          label="تاريخ التقديم"
          value={
            doctor.createdAt ? new Date(doctor.createdAt).toLocaleDateString('ar-EG') : '—'
          }
        />
      </dl>

      {clinics.length > 0 && (
        <div>
          <h4 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-200">
            <Icon name="building-2" size={15} className="text-cyan-400" />
            العيادات ({clinics.length})
          </h4>
          <ul className="space-y-2">
            {clinics.map((clinic) => (
              <li key={clinic.id} className="rounded-xl border border-white/5 bg-white/5 p-3">
                <p className="text-sm font-bold text-white">{clinic.name}</p>
                {clinic.address && <p className="text-xs text-slate-400">{clinic.address}</p>}
                {Array.isArray(clinic.workingDays) && clinic.workingDays.length > 0 && (
                  <p className="mt-1 flex flex-wrap gap-1">
                    {clinic.workingDays.map((day) => (
                      <span
                        key={day}
                        className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-300"
                      >
                        {ARABIC_DAYS[String(day).toLowerCase()] || day}
                      </span>
                    ))}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-2.5 text-[11px] leading-relaxed text-amber-200">
        <Icon name="info" size={12} className="ml-1 inline" />
        الطلب غير المفعّل يبقى في قائمة الانتظار — لا يوجد إجراء رفض نهائي في النظام الحالي.
      </p>
    </div>
  );
}

function Item({ label, value, ltr = false }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-slate-400">{label}</dt>
      <dd className="truncate text-xs font-bold text-slate-200" dir={ltr ? 'ltr' : undefined}>
        {value}
      </dd>
    </div>
  );
}

export default AdminRegistrations;
